import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../ui/dialog';
import { Calculator, TrendingUp, Target, AlertTriangle, CheckCircle2, Settings, RotateCcw } from 'lucide-react';

// Format helpers
const formatCurrency = (value, useDecimal = false) => {
  if (value === null || value === undefined) return '-';
  if (value >= 1000000) return `$${(value / 1000000).toFixed(useDecimal ? 2 : 1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(useDecimal ? 1 : 0)}K`;
  return `$${Number(value).toFixed(useDecimal ? 2 : 0)}`;
};

const formatNumber = (value, useDecimal = false) => {
  if (value === null || value === undefined) return '-';
  return useDecimal ? Number(value).toFixed(1) : Number(value).toFixed(0);
};

// Single required output metric
const RequiredMetric = ({ label, current, target, remaining, requiredPerWeek, weeksLeft, isPacing, useDecimal }) => {
  const percentComplete = target > 0 ? (current / target) * 100 : 0;
  const isOnPace = isPacing;
  
  return (
    <div className={`p-4 rounded-lg border ${isOnPace ? 'border-green-200 bg-green-50 dark:bg-green-900/10 dark:border-green-800' : 'border-yellow-200 bg-yellow-50 dark:bg-yellow-900/10 dark:border-yellow-800'}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{label}</span>
        {isOnPace ? (
          <CheckCircle2 className="h-4 w-4 text-green-500" />
        ) : (
          <AlertTriangle className="h-4 w-4 text-yellow-500" />
        )}
      </div>
      
      <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full mb-3 overflow-hidden">
        <div 
          className={`h-full rounded-full transition-all ${isOnPace ? 'bg-green-500' : 'bg-yellow-500'}`}
          style={{ width: `${Math.min(percentComplete, 100)}%` }}
        />
      </div>
      
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <span className="text-muted-foreground">Current: </span>
          <span className="font-semibold text-slate-900 dark:text-white">
            {typeof current === 'number' && current >= 1000 ? formatCurrency(current, useDecimal) : formatNumber(current, useDecimal)}
          </span>
        </div>
        <div>
          <span className="text-muted-foreground">Target: </span>
          <span className="font-semibold text-slate-900 dark:text-white">
            {typeof target === 'number' && target >= 1000 ? formatCurrency(target, useDecimal) : formatNumber(target, useDecimal)}
          </span>
        </div>
        <div>
          <span className="text-muted-foreground">Remaining: </span>
          <span className={`font-semibold ${remaining > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'}`}>
            {remaining > 0 ? (typeof remaining === 'number' && remaining >= 1000 ? formatCurrency(remaining, useDecimal) : formatNumber(remaining, useDecimal)) : '✓ Goal Met'}
          </span>
        </div>
        <div>
          <span className="text-muted-foreground">Need/Week: </span>
          <span className={`font-bold ${!isOnPace ? 'text-yellow-600 dark:text-yellow-400' : 'text-slate-900 dark:text-white'}`}>
            {requiredPerWeek > 0 ? (typeof requiredPerWeek === 'number' && requiredPerWeek >= 1000 ? formatCurrency(requiredPerWeek, useDecimal) : formatNumber(requiredPerWeek, useDecimal)) : '-'}
          </span>
        </div>
      </div>
      
      {weeksLeft > 0 && remaining > 0 && (
        <div className={`mt-3 text-xs ${isOnPace ? 'text-green-700 dark:text-green-400' : 'text-yellow-700 dark:text-yellow-400'}`}>
          {isOnPace 
            ? `On pace to hit goal with ${weeksLeft} week${weeksLeft > 1 ? 's' : ''} remaining`
            : `Need to average ${typeof requiredPerWeek === 'number' && requiredPerWeek >= 1000 ? formatCurrency(requiredPerWeek, useDecimal) : formatNumber(requiredPerWeek, useDecimal)}/week to hit goal`
          }
        </div>
      )}
    </div>
  );
};

// Settings Modal Component
const SettingsModal = ({ settings, onSettingsChange, onReset }) => {
  const [open, setOpen] = useState(false);
  const [localSettings, setLocalSettings] = useState(settings);

  const handleOpen = (isOpen) => {
    if (isOpen) {
      setLocalSettings(settings);
    }
    setOpen(isOpen);
  };

  const handleSave = () => {
    onSettingsChange(localSettings);
    setOpen(false);
  };

  const handleReset = () => {
    onReset();
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="weekly-output-settings-btn">
          <Settings className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]" data-testid="weekly-output-settings-modal">
        <DialogHeader>
          <DialogTitle>Weekly Required Output Settings</DialogTitle>
          <DialogDescription>
            Adjust display and calculation options. Changes are temporary and reset on page refresh.
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          {/* Calculation Mode */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="use-days">Use Days Remaining</Label>
              <p className="text-xs text-muted-foreground">
                Calculate using days instead of weeks
              </p>
            </div>
            <Switch
              id="use-days"
              checked={localSettings.useDaysRemaining}
              onCheckedChange={(checked) => 
                setLocalSettings(prev => ({ ...prev, useDaysRemaining: checked }))
              }
            />
          </div>
          
          {/* Decimal Precision */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="use-decimal">Show Decimal Precision</Label>
              <p className="text-xs text-muted-foreground">
                Display values with decimal points
              </p>
            </div>
            <Switch
              id="use-decimal"
              checked={localSettings.useDecimal}
              onCheckedChange={(checked) => 
                setLocalSettings(prev => ({ ...prev, useDecimal: checked }))
              }
            />
          </div>
          
          {/* Show At Risk Banner */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="show-risk">Show "At Risk" Banner</Label>
              <p className="text-xs text-muted-foreground">
                Display status alert at top of section
              </p>
            </div>
            <Switch
              id="show-risk"
              checked={localSettings.showAtRiskBanner}
              onCheckedChange={(checked) => 
                setLocalSettings(prev => ({ ...prev, showAtRiskBanner: checked }))
              }
            />
          </div>

          {/* Target Overrides */}
          <div className="border-t pt-4">
            <Label className="text-sm font-medium">Temporary Target Overrides</Label>
            <p className="text-xs text-muted-foreground mb-3">
              Adjust targets for what-if analysis (not saved to database)
            </p>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="leads-target" className="text-xs">Leads Target</Label>
                <Input
                  id="leads-target"
                  type="number"
                  value={localSettings.targetOverrides.leads || ''}
                  onChange={(e) => setLocalSettings(prev => ({
                    ...prev,
                    targetOverrides: { ...prev.targetOverrides, leads: e.target.value ? Number(e.target.value) : null }
                  }))}
                  placeholder="Use API default"
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <Label htmlFor="sales-target" className="text-xs">Sales Target</Label>
                <Input
                  id="sales-target"
                  type="number"
                  value={localSettings.targetOverrides.sales || ''}
                  onChange={(e) => setLocalSettings(prev => ({
                    ...prev,
                    targetOverrides: { ...prev.targetOverrides, sales: e.target.value ? Number(e.target.value) : null }
                  }))}
                  placeholder="Use API default"
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <Label htmlFor="revenue-target" className="text-xs">Revenue Target</Label>
                <Input
                  id="revenue-target"
                  type="number"
                  value={localSettings.targetOverrides.revenue || ''}
                  onChange={(e) => setLocalSettings(prev => ({
                    ...prev,
                    targetOverrides: { ...prev.targetOverrides, revenue: e.target.value ? Number(e.target.value) : null }
                  }))}
                  placeholder="Use API default"
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <Label htmlFor="appts-target" className="text-xs">Appointments Target</Label>
                <Input
                  id="appts-target"
                  type="number"
                  value={localSettings.targetOverrides.appointments || ''}
                  onChange={(e) => setLocalSettings(prev => ({
                    ...prev,
                    targetOverrides: { ...prev.targetOverrides, appointments: e.target.value ? Number(e.target.value) : null }
                  }))}
                  placeholder="Use API default"
                  className="h-8 text-sm"
                />
              </div>
            </div>
          </div>
        </div>
        
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleReset} className="gap-1">
            <RotateCcw className="h-3 w-3" />
            Reset
          </Button>
          <Button onClick={handleSave}>Apply Changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Default settings
const DEFAULT_SETTINGS = {
  useDaysRemaining: false,
  useDecimal: false,
  showAtRiskBanner: true,
  targetOverrides: {
    leads: null,
    sales: null,
    revenue: null,
    appointments: null,
  }
};

// Main Weekly Required Output component
export const WeeklyRequiredOutput = ({ data, loading }) => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  const handleReset = () => {
    setSettings(DEFAULT_SETTINGS);
  };

  if (loading) {
    return (
      <Card className="dark:bg-slate-800/60" data-testid="weekly-required-loading">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Calculator className="h-5 w-5 text-primary" />
            Weekly Required Output
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1,2,3,4].map(i => (
              <div key={i} className="h-32 bg-slate-200 dark:bg-slate-700 rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const metrics = data?.metrics || {};
  const period = data?.period || {};
  
  const daysElapsed = period.days_elapsed || 0;
  const daysTotal = period.days_total || 30;
  const daysRemaining = Math.max(daysTotal - daysElapsed, 0);
  const weeksRemaining = Math.ceil(daysRemaining / 7);
  const weeksInPeriod = Math.ceil(daysTotal / 7);
  const currentWeek = Math.ceil(daysElapsed / 7) || 1;
  
  const elapsedPercent = daysTotal > 0 ? daysElapsed / daysTotal : 0;

  // Apply target overrides
  const getTarget = (key, defaultValue) => {
    return settings.targetOverrides[key] ?? defaultValue;
  };

  // Use days or weeks for calculation
  const divisor = settings.useDaysRemaining ? daysRemaining : weeksRemaining;
  const divisorLabel = settings.useDaysRemaining ? 'day' : 'week';

  const buildRequiredMetrics = () => {
    const metricsList = [
      {
        key: 'leads',
        label: 'Leads',
        current: metrics.leads?.actual || 0,
        target: getTarget('leads', metrics.leads?.target || 0),
      },
      {
        key: 'appointments',
        label: 'Appointments',
        current: metrics.appointments?.actual || metrics.appointments_set?.actual || 0,
        target: getTarget('appointments', metrics.appointments?.target || metrics.appointments_set?.target || 0),
      },
      {
        key: 'sales',
        label: 'Sales',
        current: metrics.sales?.actual || 0,
        target: getTarget('sales', metrics.sales?.target || 0),
      },
      {
        key: 'revenue',
        label: 'Revenue',
        current: metrics.revenue?.actual || 0,
        target: getTarget('revenue', metrics.revenue?.target || 0),
      },
    ];

    return metricsList.map(m => {
      const remaining = Math.max(m.target - m.current, 0);
      const requiredPerPeriod = divisor > 0 ? remaining / divisor : 0;
      
      const expectedCurrent = m.target * elapsedPercent;
      const isPacing = m.current >= expectedCurrent * 0.9;
      
      return {
        ...m,
        remaining,
        requiredPerWeek: requiredPerPeriod,
        weeksLeft: settings.useDaysRemaining ? daysRemaining : weeksRemaining,
        isPacing,
      };
    });
  };

  const requiredMetrics = buildRequiredMetrics();
  
  const onPaceCount = requiredMetrics.filter(m => m.isPacing).length;
  const totalMetrics = requiredMetrics.length;
  
  // Adjusted status logic:
  // - on_pace: ALL metrics (or all but one) are pacing
  // - at_risk: At least ONE metric is pacing (some progress being made)
  // - behind: ZERO metrics are pacing (completely off track)
  const overallStatus = onPaceCount >= totalMetrics - 1 ? 'on_pace' : onPaceCount >= 1 ? 'at_risk' : 'behind';

  return (
    <Card className="dark:bg-slate-800/60" data-testid="weekly-required-output">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Calculator className="h-5 w-5 text-primary" />
            Weekly Required Output
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              Week {currentWeek} of {weeksInPeriod}
            </Badge>
            <Badge 
              className={`text-xs ${
                overallStatus === 'on_pace' ? 'bg-green-500' : 
                overallStatus === 'at_risk' ? 'bg-yellow-500' : 'bg-red-500'
              }`}
            >
              {overallStatus === 'on_pace' ? 'On Track' : overallStatus === 'at_risk' ? 'At Risk' : 'Behind Pace'}
            </Badge>
            <SettingsModal 
              settings={settings} 
              onSettingsChange={setSettings}
              onReset={handleReset}
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          {daysRemaining} days remaining • {settings.useDaysRemaining ? `${daysRemaining} ${divisorLabel}s` : `${weeksRemaining} ${divisorLabel}${weeksRemaining !== 1 ? 's' : ''}`} to hit {period.label || 'monthly'} goals
          {Object.values(settings.targetOverrides).some(v => v !== null) && (
            <span className="ml-2 text-orange-500">(using custom targets)</span>
          )}
        </p>
      </CardHeader>
      <CardContent>
        {/* Summary callout - conditionally shown */}
        {settings.showAtRiskBanner && (
          <div className={`mb-4 p-3 rounded-lg ${
            overallStatus === 'on_pace' ? 'bg-green-100 dark:bg-green-900/20 border border-green-200 dark:border-green-800' :
            overallStatus === 'at_risk' ? 'bg-yellow-100 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800' :
            'bg-red-100 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
          }`}>
            <div className="flex items-center gap-2">
              {overallStatus === 'on_pace' ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <AlertTriangle className={`h-5 w-5 ${overallStatus === 'at_risk' ? 'text-yellow-600' : 'text-red-600'}`} />
              )}
              <span className={`text-sm font-medium ${
                overallStatus === 'on_pace' ? 'text-green-700 dark:text-green-400' :
                overallStatus === 'at_risk' ? 'text-yellow-700 dark:text-yellow-400' :
                'text-red-700 dark:text-red-400'
              }`}>
                {overallStatus === 'on_pace' 
                  ? 'On pace to hit all monthly goals'
                  : overallStatus === 'at_risk'
                    ? `${onPaceCount} of ${totalMetrics} metrics on track - focus on ${totalMetrics - onPaceCount} behind`
                    : 'All metrics behind pace - increased output needed'
                }
              </span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {requiredMetrics.map(({ key: metricKey, ...metricProps }) => (
            <RequiredMetric key={metricKey} {...metricProps} useDecimal={settings.useDecimal} />
          ))}
        </div>
        
        <div className="mt-4 pt-4 border-t dark:border-slate-700">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <TrendingUp className="h-4 w-4" />
            <span>
              Based on {period.days_elapsed || 0} days elapsed ({Math.round(elapsedPercent * 100)}% of period)
              {settings.useDaysRemaining && ' • Using daily calculation mode'}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default WeeklyRequiredOutput;
